import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import bannerAuth from '@/assets/bannerAuth.jpg';
import logoYonyon from '@/assets/logoYonyon.png';

import { authResetPassword } from '@/services/authService';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();

    const email = searchParams.get('email') ?? '';
    const token = searchParams.get('token') ?? '';

    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        setError(null);

        if (!email || !token) {
            setError('Ce lien de réinitialisation est invalide.');
            return;
        }

        if (password.length < 8) {
            setError('Le mot de passe doit contenir au moins 8 caractères.');
            return;
        }

        if (!/[A-Z]/.test(password)) {
            setError('Le mot de passe doit contenir au moins une majuscule.');
            return;
        }

        if (!/[a-z]/.test(password)) {
            setError('Le mot de passe doit contenir au moins une minuscule.');
            return;
        }

        if (!/[0-9]/.test(password)) {
            setError('Le mot de passe doit contenir au moins un chiffre.');
            return;
        }

        if (password !== confirmation) {
            setError('Les deux mots de passe ne correspondent pas.');
            return;
        }

        setSubmitting(true);

        try {
            await authResetPassword({
                email,
                token,
                newPassword: password,
            });

            setSuccess(true);
        } catch (err: unknown) {
            const msg =
                err instanceof Error
                    ? err.message
                    : 'Impossible de réinitialiser le mot de passe.';

            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background-100 p-4 sm:p-6 lg:p-8">
            <div className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row gap-6 lg:h-[580px]">

                {/* PANNEAU IMAGE */}
                <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-2xl">
                    <img
                        src={bannerAuth}
                        alt="Yonyon, la communauté locale autour de Dole"
                        className="absolute inset-0 w-full h-full object-cover object-center"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/35 to-foreground-950/10" />

                    <Link
                        to="/"
                        className="absolute top-6 left-1/2 -translate-x-1/2 z-10"
                    >
                        <img
                            src={logoYonyon}
                            alt="Yonyon"
                            className="h-24 w-auto object-contain"
                        />
                    </Link>

                    <div className="relative w-full flex flex-col justify-end p-10">
                        <h1 className="font-heading text-4xl font-semibold text-white leading-tight max-w-md">
                            Retrouve ton compte.
                        </h1>

                        <p className="mt-3 text-base text-white/85 max-w-md">
                            Choisis un nouveau mot de passe et retrouve toutes tes activités
                            sur YonYon.
                        </p>
                    </div>
                </div>

                {/* FORMULAIRE */}
                <div className="flex-1 flex items-center justify-center min-h-0">
                    <div className="w-full max-w-md">

                        {/* Logo mobile */}
                        <Link
                            to="/"
                            className="lg:hidden flex items-center justify-center mb-8"
                        >
                            <img
                                src={logoYonyon}
                                alt="Yonyon"
                                className="h-24 w-auto object-cover"
                            />
                        </Link>

                        <div className="bg-white rounded-2xl shadow-sm border border-background-100/50 p-6">

                            {!success ? (
                                <>
                                    <h2 className="font-heading text-2xl font-semibold text-foreground-950">
                                        Nouveau mot de passe
                                    </h2>

                                    <p className="mt-1 text-sm text-foreground-500">
                                        Choisis ton nouveau mot de passe pour ton compte YonYon.
                                    </p>

                                    <form
                                        onSubmit={handleSubmit}
                                        className="mt-5 flex flex-col gap-3"
                                    >
                                        {/* Nouveau mot de passe */}
                                        <div>
                                            <label
                                                htmlFor="reset-password"
                                                className="block text-sm font-medium text-foreground-700 mb-1.5"
                                            >
                                                Nouveau mot de passe
                                            </label>

                                            <div className="relative">
                                                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 pointer-events-none" />

                                                <input
                                                    id="reset-password"
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    autoComplete="new-password"

                                                    className="w-full pl-10 pr-10 py-2.5 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                                                />

                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword((current) => !current)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer"
                                                    aria-label={
                                                        showPassword
                                                            ? 'Masquer le mot de passe'
                                                            : 'Afficher le mot de passe'
                                                    }
                                                >
                                                    <i
                                                        className={
                                                            showPassword
                                                                ? 'ri-eye-off-line'
                                                                : 'ri-eye-line'
                                                        }
                                                    />
                                                </button>
                                            </div>

                                            <p className="mt-1.5 text-xs text-foreground-400">
                                                8 caractères minimum, avec une majuscule, une minuscule
                                                et un chiffre.
                                            </p>
                                        </div>

                                        {/* Confirmation */}
                                        <div>
                                            <label
                                                htmlFor="reset-confirm-password"
                                                className="block text-sm font-medium text-foreground-700 mb-1.5"
                                            >
                                                Confirmer le mot de passe
                                            </label>

                                            <div className="relative">
                                                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 pointer-events-none" />

                                                <input
                                                    id="reset-confirm-password"
                                                    type="password"
                                                    value={confirmation}
                                                    onChange={(e) => setConfirmation(e.target.value)}
                                                    autoComplete="new-password"
                                                    required
                                                    className="w-full pl-10 pr-3 py-2.5 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                                                />
                                            </div>
                                        </div>

                                        {error && (
                                            <div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-primary-50 border border-primary-200 text-primary-700 text-sm">
                                                <i className="ri-error-warning-line mt-0.5" />
                                                <span>{error}</span>
                                            </div>
                                        )}

                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className="w-full px-4 py-3 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {submitting && (
                                                <i className="ri-loader-4-line animate-spin" />
                                            )}

                                            Réinitialiser le mot de passe
                                        </button>
                                    </form>
                                </>
                            ) : (
                                <div className="text-center">

                                    <div className="mx-auto h-12 w-12 rounded-full bg-accent-50 flex items-center justify-center">
                                        <i className="ri-check-line text-accent-500 text-xl" />
                                    </div>

                                    <h2 className="mt-4 font-heading text-2xl font-semibold text-foreground-950">
                                        Mot de passe modifié
                                    </h2>

                                    <p className="mt-2 text-sm text-foreground-500">
                                        Ton nouveau mot de passe est maintenant actif.
                                    </p>

                                    <Link
                                        to="/auth"
                                        className="mt-5 inline-flex w-full items-center justify-center px-4 py-3 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors"
                                    >
                                        Se connecter
                                    </Link>
                                </div>
                            )}

                        </div>

                        {!success && (
                            <Link
                                to="/auth"
                                className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
                            >
                                <i className="ri-arrow-left-line" />
                                Retour à la connexion
                            </Link>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
}